/**
 * Display-only identifier formatting for FlowV2 UI.
 * Do not persist these strings — keep raw names for ToolID, port ids, Condition, etc.
 */

/**
 * Insert spaces into PascalCase / camelCase identifiers for display.
 * Preserves acronyms: CaptureDTMF → Capture DTMF, XMLHttpRequest → XML Http Request.
 * Already-spaced strings and single words are unchanged.
 * @param {unknown} text
 * @returns {string}
 */
export function formatDisplayName(text) {
    const t = String(text ?? '').trim();
    if (!t)
        return '';
    return t
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2');
}

/**
 * Short type name for schema field titles: last dotted segment only.
 * Does not PascalCase-split (ResultType stays ResultType).
 * @param {unknown} type
 * @returns {string}
 */
export function formatTypeDisplayName(type) {
    const t = String(type ?? '').trim();
    if (!t)
        return '';
    const i = t.lastIndexOf('.');
    return i >= 0 ? t.slice(i + 1) : t;
}

/**
 * Argument / output field title: spaced name + short type.
 * When typeDisplay is empty (e.g. Platform.Schema.*), omit the ": type" suffix.
 * @param {unknown} name
 * @param {unknown} type
 * @param {{ out?: boolean }} [opts]
 * @returns {string}
 */
export function formatArgFieldTitle(name, type, opts) {
    const n = formatDisplayName(name);
    const ty = formatTypeDisplayName(type);
    const out = opts?.out ? ' (out)' : '';
    if (!n)
        return ty ? `${ty}${out}` : (out ? out.trim() : '');
    if (!ty)
        return `${n}${out}`;
    return `${n}: ${ty}${out}`;
}
